import { computed, defineComponent, ref } from 'vue'
import type { ExtractPropTypes, CSSProperties, ComponentPublicInstance, PropType } from 'vue'
import classNames from '../_antd_util/classNames'
import { ElTable, ElTableColumn, ElPagination } from 'element-plus'
import type { TableColumnCtx } from 'element-plus'

import { formatDate, number2string, getSingleCodeContent, getMultiCodeContent } from './utils'
import dayjs from 'dayjs'

import Space from '../Space'
import OrionError from '../../error/OrionError'

import useColumns from './hooks/useColumns'
import useCode from './hooks/useCode'

type rowClassNameFn = (data: { rowData: any; rowIndex: number }) => string
type rowStyleFn = (data: { rowData: any; rowIndex: number }) => CSSProperties
type cellClassNameFn = (data: { rowData: any; column: { name: string; head: string }; rowIndex: number; columnIndex: number }) => string
type cellStyleFn = (data: { rowData: any; column: { name: string; head: string }; rowIndex: number; columnIndex: number }) => CSSProperties

export const pagetableProps = () => ({
  data: Array,
  tailData: Object,
  pagination: Object as PropType<{
    currentPage?: number
    pageSize?: number
    pageSizes?: number[]
    total?: number
  }>,
  paginationTeleported: {
    type: Boolean,
    default: true,
  },
  paginationPagerCount: Number,
  paginationShowTotal: {
    type: Boolean,
    default: true,
  },
  paginationShowSizes: {
    type: Boolean,
    default: true,
  },
  paginationShowJumber: {
    type: Boolean,
    default: true,
  },
  showRowNumber: {
    type: Boolean,
    default: true,
  },
  selectionMode: {
    type: Boolean,
    default: false,
  },
  currentRowMode: {
    type: Boolean,
    default: false,
  },
  size: String as PropType<'' | 'large' | 'default' | 'small'>,
  rowClassName: [String, Function] as PropType<string | rowClassNameFn>,
  rowStyle: [Object, Function] as PropType<CSSProperties | rowStyleFn>,
  cellClassName: [String, Function] as PropType<string | cellClassNameFn>,
  cellStyle: [Object, Function] as PropType<CSSProperties | cellStyleFn>,
})

export type PagetableProps = Partial<ExtractPropTypes<ReturnType<typeof pagetableProps>>>

export interface PagetableExpose {
  clearSelection: () => void
  toggleRowSelection: (row: any, selected?: boolean) => void
  toggleAllSelection: () => void
  getSelectionRows: () => any[]
  setCurrentRow: (row: any) => void
}

export type PagetableInstance = ComponentPublicInstance<PagetableProps, PagetableExpose>

interface SummaryMethodProps<T = any> {
  columns: TableColumnCtx<T>[]
  data: T[]
}

export default defineComponent({
  name: 'OPagetable',
  inheritAttrs: false,
  props: pagetableProps(),
  emits: [
    'selectionChange',
    'currentRowChange',
    'update:pagination',
    'paginationChange',
    'rowClick',
    'rowDblclick',
    'cellClick',
    'cellDblclick',
    'select',
    'select-all',
  ],
  setup(props, { slots, attrs, emit, expose }) {
    const elTableRef = ref()

    const style = computed(() => {
      const rowStyle: CSSProperties = {}

      return rowStyle
    })

    const columns = useColumns(slots)

    const leafColumns4ShowSummary = computed(() => {
      let r = []
      if (props.showRowNumber) {
        r.push({ type: '__showRowNumber__' })
      }

      if (props.selectionMode) {
        r.push({ type: '__selectionMode__' })
      }

      function flat(columns) {
        if (!columns) {
          return
        }

        for (let i = 0; i < columns.length; i++) {
          const c = columns[i]
          if (c.type === 'OPagetableColumngroup') {
            flat(c.children)
          } else {
            r.push({
              ...c,
            })
          }
        }
      }
      flat(columns.value)

      return r
    })

    const showSummary = computed(() => {
      function existTail(columns) {
        if (!columns) {
          return false
        }

        for (let i = 0; i < columns.length; i++) {
          const c = columns[i]
          if (c.type === 'OPagetableColumngroup') {
            if (existTail(c.children)) {
              return true
            }
          } else {
            if (c.tail === '' || c.tail === true || c.tail) {
              return true
            }
          }
        }

        return false
      }

      return existTail(columns.value)
    })

    const clearSelection = () => {
      elTableRef.value.clearSelection()
    }

    const getSelectionRows = () => {
      return elTableRef.value.getSelectionRows()
    }

    const toggleRowSelection = (...args) => {
      return elTableRef.value.toggleRowSelection(...args)
    }

    const toggleAllSelection = () => {
      return elTableRef.value.toggleAllSelection()
    }

    const setCurrentRow = (...args) => {
      return elTableRef.value.setCurrentRow(...args)
    }

    const scrollTo = (...args) => {
      return elTableRef.value.scrollTo(...args)
    }

    expose({
      clearSelection,
      getSelectionRows,
      toggleRowSelection,
      toggleAllSelection,
      setCurrentRow,
      scrollTo,
    })

    return () => {
      const { data } = props
      const { selectionMode = false } = props
      const { pagination, paginationTeleported, paginationPagerCount, paginationShowTotal, paginationShowSizes, paginationShowJumber } = props
      const { showRowNumber = true } = props
      const { currentRowMode = false } = props
      const { size = 'default' } = props
      const { rowClassName, rowStyle, cellClassName, cellStyle } = props

      const getSummaries = (param: SummaryMethodProps) => {
        const tailData = computed(() => props.tailData)

        return leafColumns4ShowSummary.value.map((c) => {
          if (c.type === '__showRowNumber__') {
            return 'Σ'
          }

          if (c.type === '__selectionMode__') {
            return
          }

          if (!tailData.value) {
            return
          }

          const columnName = c.name
          const columnTail = c.tail

          // 没配置 tail
          if (columnTail == null) {
            return
          }

          // 明确不显示 tail
          if (columnTail === false) {
            return
          }

          if (columnTail === '' || columnTail === true) {
            if (tailData.value[columnName] != null) {
              return tailData.value[columnName]
            }
          } else if (typeof columnTail === 'string') {
            return columnTail.replace('{}', tailData.value[columnName])
          } else {
            throw new OrionError(`列[${columnName}]的tail属性配置错误，只能是 Boolean、String 类型，当前是[${typeof columnTail}]类型。`)
          }

          return
        })
      }

      const cn = classNames('orion-pagetbale')

      const renderIndexColumn = () => {
        const headAlign = 'center'

        return (
          <ElTableColumn
            type={'index'}
            label="No"
            headerAlign={headAlign}
            align={'center'}
            width={56}
            fixed={slots.expandedRowRender ? false : 'left'}
            showOverflowTooltip={true}
            index={(index: number) => {
              return (pagination.currentPage - 1) * pagination.pageSize + index + 1
            }}
          ></ElTableColumn>
        )
      }

      const renderSelectionColumn = () => {
        return (
          <ElTableColumn type={'selection'} headerAlign={'center'} align={'center'} width={40} fixed={slots.expandedRowRender ? false : 'left'}></ElTableColumn>
        )
      }

      const renderStringColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign, width, fixed } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              header: () => column.children?.head?.(),
            }}
          ></ElTableColumn>
        )
      }

      const renderLinkColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign, width, fixed } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              default: (scope) => {
                const { row, column } = scope
                return <span class="cell-linkcolumn">{row[column.property]}</span>
              },
              header: () => column.children?.head?.(),
            }}
          ></ElTableColumn>
        )
      }

      const renderNumberColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign = 'right', width, fixed } = column
        const { thousandsSeparator = true, precision = -1, percentage = false } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              default: (scope) => {
                const { row, column } = scope
                return (
                  <span class="cell-numbercolumn">
                    {number2string(row[column.property], thousandsSeparator, precision, percentage === true || percentage === '')}
                  </span>
                )
              },
              header: () => column.children?.head?.(),
            }}
          ></ElTableColumn>
        )
      }

      const renderDateColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign = 'center', width, fixed, sourceFormat, format = 'YYYYMMDDHHmmss' } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              default: (scope) => {
                const { row, column } = scope
                const cellValue = row[column.property]

                if (cellValue instanceof Date || dayjs.isDayjs(cellValue)) {
                  return <div>{formatDate(cellValue, format)}</div>
                } else {
                  if (sourceFormat && format) {
                    return <div>{formatDate(cellValue, sourceFormat, format)}</div>
                  } else {
                    return <div>{cellValue}</div>
                  }
                }
              },
              header: () => column.children?.head?.(),
            }}
          ></ElTableColumn>
        )
      }

      const renderSingleSelectColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign, width, fixed } = column
        const { code } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              default: (scope) => {
                const { row, column } = scope
                const cellValue = row[column.property]

                let codeContent
                const codeData = useCode(code)
                if (!codeData.value) {
                  codeContent = null
                } else {
                  codeContent = getSingleCodeContent(codeData.value, cellValue)
                }

                return <span class="cell-singleselect">{codeContent}</span>
              },
              header: () => column.children?.head?.(),
            }}
          ></ElTableColumn>
        )
      }

      const renderMultiSelectColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign, width, fixed } = column
        const { code } = column
        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            showOverflowTooltip={true}
            v-slots={{
              default: (scope) => {
                const { row, column } = scope
                const cellValue = row[column.property]

                let codeContent
                const codeData = useCode(code)
                if (!codeData.value) {
                  codeContent = null
                } else {
                  codeContent = getMultiCodeContent(codeData.value, cellValue)
                }

                return <span class="cell-multiselect">{codeContent}</span>
              },
              header: () => column.children?.head?.(),
            }}
          ></ElTableColumn>
        )
      }

      const renderButtongroupColumn = (column) => {
        const { name, head, headAlign = 'center', bodyAlign, width, fixed } = column

        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            align={bodyAlign}
            prop={name}
            width={width}
            fixed={fixed}
            v-slots={{
              default: (scope) => {
                // { row: any, column: any, $index: number }
                const children = column.children?.default?.({
                  rowData: scope.row,
                  column,
                  rowIndex: scope.$index,
                })

                return (
                  <div class="cell-buttongroup">
                    <Space>{children}</Space>
                  </div>
                )
              },
              header: () => column.children?.head?.(),
            }}
          ></ElTableColumn>
        )
      }

      const renderColumngroupColumn = (column) => {
        const { head, headAlign = 'center' } = column
        const { children } = column

        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            v-slots={{
              default: (scope) => {
                return renderColumns(children)
              },
            }}
          ></ElTableColumn>
        )
      }

      const renderDiyColumn = (column) => {
        const { name, head, headAlign = 'center', width, fixed } = column

        return (
          <ElTableColumn
            label={head}
            headerAlign={headAlign}
            prop={name}
            width={width}
            fixed={fixed}
            v-slots={{
              default: (scope) => {
                // { row: any, column: any, $index: number }
                const children = column.children?.default?.({
                  rowData: scope.row,
                  column,
                  rowIndex: scope.$index,
                })

                return <div class="cell-diy">{children} </div>
              },
              header: () => column.children?.head?.(),
            }}
          ></ElTableColumn>
        )
      }

      const renderColumns = (columns) => {
        return columns?.map((c) => {
          if (c.type === 'OPagetableColumnString') {
            return renderStringColumn(c)
          } else if (c.type === 'OPagetableColumnLink') {
            return renderLinkColumn(c)
          } else if (c.type === 'OPagetableColumnNumber') {
            return renderNumberColumn(c)
          } else if (c.type === 'OPagetableColumnDate') {
            return renderDateColumn(c)
          } else if (c.type === 'OPagetableColumnSingleselect') {
            return renderSingleSelectColumn(c)
          } else if (c.type === 'OPagetableColumnMultiselect') {
            return renderMultiSelectColumn(c)
          } else if (c.type === 'OPagetableColumnButtongroup') {
            return renderButtongroupColumn(c)
          } else if (c.type === 'OPagetableColumngroup') {
            return renderColumngroupColumn(c)
          } else if (c.type === 'OPagetableColumnDiy') {
            return renderDiyColumn(c)
          } else {
            throw new OrionError(`OPagetable不能正确渲染'${c.type.name}'子组件。`)
          }
        })
      }

      return (
        <div class={[cn, attrs.class]} style={[style.value, attrs.style as CSSProperties]}>
          <div class="pagetable-wrapper">
            <ElTable
              showSummary={showSummary.value}
              summaryMethod={getSummaries}
              border
              ref={elTableRef}
              data={data}
              height={'100%'}
              style={{ width: '100%' }}
              onSelection-change={(rows) => emit('selectionChange', rows)}
              highlight-current-row={currentRowMode}
              onCurrent-change={(currentRow, oldCurrentRow) => emit('currentRowChange', currentRow, oldCurrentRow)}
              onRow-click={(row: any, column: any, event: Event) => emit('rowClick', row, { name: column.property, head: column.label }, event)}
              onRow-dblclick={(row: any, column: any, event: Event) => emit('rowDblclick', row, { name: column.property, head: column.label }, event)}
              onCell-click={(row: any, column: any, cell: any, event: Event) =>
                emit('cellClick', row, { name: column.property, head: column.label }, cell, event)
              }
              onCell-dblclick={(row: any, column: any, cell: any, event: Event) =>
                emit('cellDblclick', row, { name: column.property, head: column.label }, cell, event)
              }
              onSelect={(selection, row) => emit('select', selection, row)}
              onSelect-all={(selection) => emit('select-all', selection)}
              size={size}
              rowClassName={(data: { row: any; rowIndex: number }) => {
                if (typeof rowClassName === 'string') {
                  return rowClassName
                } else if (typeof rowClassName === 'function') {
                  return rowClassName({ rowData: data.row, rowIndex: data.rowIndex })
                } else {
                  return 'row-class-name_not_valid'
                }
              }}
              rowStyle={(data: { row: any; rowIndex: number }) => {
                if (typeof rowStyle === 'function') {
                  return rowStyle({ rowData: data.row, rowIndex: data.rowIndex })
                } else if (typeof rowStyle === 'object') {
                  return rowStyle
                } else {
                  return {}
                }
              }}
              cellClassName={(data: { row: any; column: any; rowIndex: number; columnIndex: number }) => {
                if (typeof cellClassName === 'string') {
                  return cellClassName
                } else if (typeof cellClassName === 'function') {
                  return cellClassName({
                    rowData: data.row,
                    column: { name: data.column.property, head: data.column.label },
                    rowIndex: data.rowIndex,
                    columnIndex: data.columnIndex,
                  })
                } else {
                  return 'cell-class-name_not_valid'
                }
              }}
              cellStyle={(data: { row: any; column: any; rowIndex: number; columnIndex: number }) => {
                if (typeof cellStyle === 'function') {
                  return cellStyle({
                    rowData: data.row,
                    column: { name: data.column.property, head: data.column.label },
                    rowIndex: data.rowIndex,
                    columnIndex: data.columnIndex,
                  })
                } else if (typeof cellStyle === 'object') {
                  return cellStyle
                } else {
                  return {}
                }
              }}
            >
              {slots.expandedRowRender && (
                <ElTableColumn
                  type="expand"
                  fixed={false}
                  v-slots={{
                    default: (scope) => {
                      // { row: any, column: any, $index: number }
                      const children = slots.expandedRowRender?.({
                        rowData: scope.row,
                        rowIndex: scope.$index,
                      })

                      return <div class="row-expand">{children}</div>
                    },
                  }}
                ></ElTableColumn>
              )}
              {showRowNumber && renderIndexColumn()}
              {selectionMode && renderSelectionColumn()}
              {renderColumns(columns.value)}
            </ElTable>
          </div>

          <div class="pagination-wrapper">
            <ElPagination
              currentPage={pagination.currentPage}
              onUpdate:current-page={(e) => {
                emit('update:pagination', {
                  ...pagination,
                  currentPage: e,
                })
                emit('paginationChange', e, pagination.pageSize)
              }}
              pageSize={pagination.pageSize}
              pageSizes={pagination.pageSizes}
              onUpdate:page-size={(e) => {
                emit('update:pagination', {
                  ...pagination,
                  pageSize: e,
                })
                emit('paginationChange', pagination.currentPage, e)
              }}
              total={pagination.total || 0}
              pagerCount={paginationPagerCount}
              layout={`${paginationShowTotal ? 'total,' : ''} ->, ${paginationShowSizes ? 'sizes,' : ''} prev, pager, next ${
                paginationShowJumber ? ', jumper' : ''
              }`}
              background
              size={size}
              teleported={paginationTeleported}
            ></ElPagination>
          </div>
        </div>
      )
    }
  },
})
